/* eslint-disable @typescript-eslint/naming-convention */
import { isValidPhoneNumber } from 'libphonenumber-js';
import * as Yup from 'yup';

import { Organization } from '../../../../../services/identity/types/UserProfile';
import {
  checkFutureDate,
  checkValidDate,
  isValidAddress,
  isValidDate,
  isValidEmail,
  isValidName,
  isValidZipCode,
} from '../../../../../Utils/validation';

export function atLeastOneFieldNotEmpty(fields: (string | undefined)[]): boolean {
  return fields.some((field) => !!field);
}

export const validationSchema = (addSecondaryInsurance: boolean, isQuickAdd: boolean, isCollectorAddUser = false): any => {
  const requireCoreIdentity = isQuickAdd || isCollectorAddUser;
  return Yup.object().shape({
    profile_img: Yup.string(),
    first_name: Yup.string().test('test-name', 'Enter Valid First Name', (value: any) => {
      if (requireCoreIdentity) {
        return value && isValidName(value);
      }
      if (value && !isValidName(value)) {
        return false;
      }
      return true;
    }),
    middle_name: Yup.string().test('test-name', 'Enter Valid Middle Name', (value: any) => {
      if (value && !isValidName(value)) {
        return false;
      }
      return true;
    }),
    last_name: Yup.string().test('test-name', 'Enter Valid Last Name', (value: any) => {
      if (requireCoreIdentity) {
        return value && isValidName(value);
      }
      if (value && !isValidName(value)) {
        return false;
      }
      return true;
    }),
    date_of_birth: Yup.string()
      .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value: any) => {
        if (requireCoreIdentity) {
          const values = value?.split('/') || [];
          return value && isValidDate(value) ? true : value && checkValidDate(values[0], values[1], values[2]);
        }
        if (value && !isValidDate(value)) {
          return false;
        }
        if (value) {
          const values = value?.split('/');
          if (!checkValidDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      })
      .test('test-future', 'The Date of Birth can not be in the future.', (value: any) => {
        if (value) {
          const values = value?.split('/');
          if (!checkFutureDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      }),
    language: Yup.array(),
    address1: Yup.string().test(
      'test-name',
      'Valid Address is required when at least one Address field is provided',
      function () {
        const { parent } = this;
        const { address1, address2, city, state, zip_code } = parent as any;
        if (atLeastOneFieldNotEmpty([address1, address2, city, state, zip_code])) {
          return !!address1 && isValidAddress(address1);
        }
        return true;
      },
    ),
    address2: Yup.string(),
    city: Yup.string().test(
      'test-required',
      'City is required when at least one Address field is provided',
      function () {
        const { parent } = this;
        const { address1, address2, city, state, zip_code } = parent as any;
        if (atLeastOneFieldNotEmpty([address1, address2, city, state, zip_code])) {
          return !!city;
        }
        return true;
      },
    ),
    state: Yup.string().test(
      'test-required',
      'State is required when at least one Address field is provided',
      function () {
        const { parent } = this;
        const { address1, address2, city, state, zip_code } = parent as any;
        if (atLeastOneFieldNotEmpty([address1, address2, city, state, zip_code])) {
          return !!state;
        }
        return true;
      },
    ),
    zip_code: Yup.string().test(
      'test-name',
      'Valid Zip Code is required when at least one Address field is provided',
      function () {
        const { parent } = this;
        const { address1, address2, city, state, zip_code } = parent as any;
        if (atLeastOneFieldNotEmpty([address1, address2, city, state, zip_code])) {
          return !!zip_code && isValidZipCode(zip_code);
        }
        return true;
      },
    ),
    phone_number: Yup.string().test('test-name', 'Enter Valid Phone Number', (value: any) => {
      if (value && !isValidPhoneNumber(value)) {
        return false;
      }
      return true;
    }),
    email: isQuickAdd
      ? Yup.string().test('test-name', 'Enter Valid Email', (value: any) => {
          if (value && !isValidEmail(value)) {
            return false;
          }
          return true;
        })
      : Yup.string()
          .required('Email is required')
          .test('test-name', 'Enter Valid Email', (value: any) => {
            if (value && !isValidEmail(value)) {
              return false;
            }
            return true;
          }),
    organization: isCollectorAddUser
      ? Yup.array()
      : isQuickAdd
        ? Yup.array()
        : Yup.array().min(1, 'Organization is required').required('Organization is required'),
    insurance_through: Yup.string(),
    insurance_carrier_id: Yup.number().when(['insurance_through'], {
      is: (val: string) => !!val && val !== 'none',
      then: Yup.number().required('Insurance carrier is required'),
      otherwise: Yup.number(),
    }),
    // InsuranceFormSection uses insurance_phone_number; InsuranceFields uses iPhoneNumber — accept either.
    insurance_phone_number: Yup.string().when(['insurance_through'], {
      is: (val: string) => !!val && val !== 'none',
      then: Yup.string()
        .test('pri-phone-required', 'Enter Valid Phone Number', function (value) {
          const p = this.parent as Record<string, unknown>;
          const modern = String(value ?? '').trim();
          const legacy = String(p.iPhoneNumber ?? '').trim();
          return !!(modern || legacy);
        })
        .test('pri-phone-valid', 'Enter Valid Phone Number', function (value) {
          const p = this.parent as Record<string, unknown>;
          const chosen =
            String(value ?? '').trim() || String(p.iPhoneNumber ?? '').trim();
          if (!chosen) return true;
          return isValidPhoneNumber(chosen);
        }),
      otherwise: Yup.string(),
    }),
    iPhoneNumber: Yup.string(),
    member_id: Yup.string().when(['insurance_through'], {
      is: (val: string) => !!val && val !== 'none',
      then: Yup.string()
        .required('Member ID is required')
        .test('no-empty-space', 'Member ID cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    group_number: Yup.string().nullable(),
    insurance_address: Yup.string().when(['insurance_through'], {
      is: (val: string) => !!val && val !== 'none',
      then: Yup.string()
        .required('Insurance Address is required')
        .test('no-empty-space', 'Insurance Address cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    insurance_zip_code: Yup.string().when(['insurance_through'], {
      is: (val: string) => !!val && val !== 'none',
      then: Yup.string()
        .required('Insurance Zip Code is required')
        .test('no-empty-space', 'Insurance Zip Code cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    insurance_city: Yup.string().when(['insurance_through'], {
      is: (val: string) => !!val && val !== 'none',
      then: Yup.string()
        .required('Insurance City is required')
        .test('no-empty-space', 'Insurance City cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    insurance_state: Yup.string().when(['insurance_through'], {
      is: (val: string) => !!val && val !== 'none',
      then: Yup.string()
        .required('Insurance State is required')
        .test('no-empty-space', 'Insurance State cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    iFirstName: Yup.string(),
    iMiddleName: Yup.string(),
    iLastName: Yup.string(),
    iDateOfBirth: Yup.string()
      .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value: any) => {
        if (value && !isValidDate(value)) {
          return false;
        }
        if (value) {
          const values = value?.split('/');
          if (!checkValidDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      })
      .test('test-future', 'The Date of Birth can not be in the future.', (value: any) => {
        if (value) {
          const values = value?.split('/');
          if (!checkFutureDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      }),
    iAddress1: Yup.string(),
    iCity: Yup.string(),
    iState: Yup.string(),
    iZipCode: Yup.string().test('test-name', 'Not valid zipcode', (value: any) => {
      if (value && !isValidZipCode(value)) {
        return false;
      }
      return true;
    }),
    state_funded_coverage_status: Yup.string().when('insurance_through', {
      is: (val: string) => val && val.trim() !== '' && val !== 'none',
      then: Yup.string().required(
        'Do you currently have insurance coverage through a state-funded program such as Medicaid?****',
      ),
      otherwise: Yup.string(),
    }),
    secondary_insurance_carrier_id: Yup.number().when(['secondary_insurance_through'], {
      is: (val: string) => !!val && val !== 'none' && addSecondaryInsurance,
      then: Yup.number()
        .required('Secondary Insurance carrier ID is required')
        .test('notZero', 'Secondary Insurance carrier ID is required', function (value) {
          return value !== 0;
        }),
      otherwise: Yup.number().nullable(),
    }),
    secondary_member_id: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => !!val && val !== 'none' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Member ID is required'),
      otherwise: Yup.string(),
    }),
    secondary_group_number: Yup.string().nullable(),
    secondary_insurance_address: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => !!val && val !== 'none' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Address is required')
        .test('no-empty-space', 'Secondary Insurance Address cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_city: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => !!val && val !== 'none' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance City is required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_state: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => !!val && val !== 'none' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance State is required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_zip_code: Yup.string()
      .when(['secondary_insurance_through'], {
        is: (val: string) => !!val && val !== 'none' && addSecondaryInsurance,
        then: Yup.string().required('Secondary Insurance ZipCode is required'),
        otherwise: Yup.string(),
      })
      .test('test-name', 'Not valid zipcode', (value) => {
        if (value && !isValidZipCode(value)) {
          return false;
        }
        return true;
      }),
    // InsuranceFormSection uses secondary_insurance_*; SecondaryInsuranceFields uses secondary_i* — accept either.
    secondary_insurance_phone_number: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => !!val && val !== 'none' && addSecondaryInsurance,
      then: Yup.string()
        .test('sec-phone-required', 'Secondary Insurance phone number is required', function (value) {
          const p = this.parent as Record<string, unknown>;
          const modern = String(value ?? '').trim();
          const legacy = String(p.secondary_iPhoneNumber ?? '').trim();
          return !!(modern || legacy);
        })
        .test('sec-phone-valid', 'Enter Valid Phone Number', function (value) {
          const p = this.parent as Record<string, unknown>;
          const chosen =
            String(value ?? '').trim() || String(p.secondary_iPhoneNumber ?? '').trim();
          if (!chosen) return true;
          return isValidPhoneNumber(chosen);
        }),
      otherwise: Yup.string(),
    }),
    secondary_iPhoneNumber: Yup.string(),
    secondary_insurance_first_name: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => !!val && val !== 'none' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .test('sec-fn-required', 'Secondary Insurance Holder First Name is required', function (value) {
          const p = this.parent as Record<string, unknown>;
          const modern = String(value ?? '').trim();
          const legacy = String(p.secondary_iFirstName ?? '').trim();
          return !!(modern || legacy);
        })
        .test('sec-fn-valid', 'Enter Valid First Name', function (value) {
          const p = this.parent as Record<string, unknown>;
          const chosen =
            String(value ?? '').trim() || String(p.secondary_iFirstName ?? '').trim();
          if (!chosen) return true;
          return isValidName(chosen);
        }),
      otherwise: Yup.string(),
    }),
    secondary_iFirstName: Yup.string(),
    secondary_iMiddleName: Yup.string(),
    secondary_insurance_last_name: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => !!val && val !== 'none' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .test('sec-ln-required', 'Secondary Insurance Holder Last Name is required', function (value) {
          const p = this.parent as Record<string, unknown>;
          const modern = String(value ?? '').trim();
          const legacy = String(p.secondary_iLastName ?? '').trim();
          return !!(modern || legacy);
        })
        .test('sec-ln-valid', 'Enter Valid Last Name', function (value) {
          const p = this.parent as Record<string, unknown>;
          const chosen =
            String(value ?? '').trim() || String(p.secondary_iLastName ?? '').trim();
          if (!chosen) return true;
          return isValidName(chosen);
        }),
      otherwise: Yup.string(),
    }),
    secondary_iLastName: Yup.string(),
    secondary_insurance_date_of_birth: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => !!val && val !== 'none' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .test('sec-dob-required', 'Secondary Insurance Holder Date of birth is required', function (value) {
          const p = this.parent as Record<string, unknown>;
          const modern = String(value ?? '').trim();
          const legacy = String(p.secondary_iDateOfBirth ?? '').trim();
          return !!(modern || legacy);
        })
        .test('sec-dob-valid', 'Enter Valid Date  MM/DD/YYYY', function (value) {
          const p = this.parent as Record<string, unknown>;
          const chosen =
            String(value ?? '').trim() || String(p.secondary_iDateOfBirth ?? '').trim();
          if (!chosen) return true;
          if (!isValidDate(chosen)) return false;
          const datevalues = chosen.split('/');
          return checkValidDate(datevalues[0], datevalues[1], datevalues[2]);
        })
        .test('sec-dob-future', 'The Secondary Date of Birth can not be in the future.', function (value) {
          const p = this.parent as Record<string, unknown>;
          const chosen =
            String(value ?? '').trim() || String(p.secondary_iDateOfBirth ?? '').trim();
          if (!chosen) return true;
          const datevalues = chosen.split('/');
          return checkFutureDate(datevalues[0], datevalues[1], datevalues[2]);
        }),
      otherwise: Yup.string(),
    }),
    secondary_iDateOfBirth: Yup.string(),
    secondary_insurance_address1: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => !!val && val !== 'none' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .test('sec-hold-addr-required', 'Secondary Insurance Holder Address is required', function (value) {
          const p = this.parent as Record<string, unknown>;
          const modern = String(value ?? '').trim();
          const legacy = String(p.secondary_iAddress1 ?? '').trim();
          return !!(modern || legacy);
        })
        .test('sec-hold-addr-space', 'Secondary Insurance Holder Address cannot be empty spaces', function (value) {
          const p = this.parent as Record<string, unknown>;
          const chosen =
            String(value ?? '').trim() || String(p.secondary_iAddress1 ?? '').trim();
          if (!chosen) return true;
          return !/^\s+$/.test(chosen);
        }),
      otherwise: Yup.string(),
    }),
    secondary_iAddress1: Yup.string(),
    secondary_insurance_city1: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => !!val && val !== 'none' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string().test(
        'sec-hold-city-required',
        'Secondary Insurance Holder city Required',
        function (value) {
          const p = this.parent as Record<string, unknown>;
          const modern = String(value ?? '').trim();
          const legacy = String(p.secondary_iCity ?? '').trim();
          return !!(modern || legacy);
        },
      ),
      otherwise: Yup.string(),
    }),
    secondary_iCity: Yup.string(),
    secondary_insurance_state1: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => !!val && val !== 'none' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string().test(
        'sec-hold-state-required',
        'Secondary Insurance Holder state Required',
        function (value) {
          const p = this.parent as Record<string, unknown>;
          const modern = String(value ?? '').trim();
          const legacy = String(p.secondary_iState ?? '').trim();
          return !!(modern || legacy);
        },
      ),
      otherwise: Yup.string(),
    }),
    secondary_iState: Yup.string(),
    secondary_insurance_zip_code1: Yup.string()
      .when(['secondary_insurance_through'], {
        is: (val: string) => !!val && val !== 'none' && val !== 'myself' && addSecondaryInsurance,
        then: Yup.string()
          .test('sec-hold-zip-required', 'Secondary Insurance Holder ZipCode is required', function (value) {
            const p = this.parent as Record<string, unknown>;
            const modern = String(value ?? '').trim();
            const legacy = String(p.secondary_iZipCode ?? '').trim();
            return !!(modern || legacy);
          })
          .test('sec-hold-zip-valid', 'Not valid zipcode', function (value) {
            const p = this.parent as Record<string, unknown>;
            const chosen =
              String(value ?? '').trim() || String(p.secondary_iZipCode ?? '').trim();
            if (!chosen) return true;
            return isValidZipCode(chosen);
          }),
        otherwise: Yup.string(),
      }),
    secondary_iZipCode: Yup.string().test('test-name', 'Not valid zipcode', (value: any) => {
      if (value && !isValidZipCode(value)) {
        return false;
      }
      return true;
    }),
    ...(isCollectorAddUser
      ? {
          client_tag_ids: Yup.array()
            .of(Yup.string())
            .min(1, 'Select at least one client tag')
            .required('Client tags are required'),
        }
      : {
          organizations: Yup.array()
            .min(1, 'Organization is required')
            .test('has-practice-or-division', 'Each organization must have at least one practice or division', (value) => {
              if (!value) {
                return true; // Allow null or undefined values
              }

              const organizations = value as Organization[];

              const hasPracticeOrDivision = organizations.every(
                (org) => org.divisions?.length > 0 || org.practices?.length > 0,
              );
              return hasPracticeOrDivision;
            }),
        }),
  });
};
